/**
 * PushTaskDetail.tsx — 推送任务详情抽屉（注册名 push-task-detail）
 *
 * 移植自原型 design/Novel-admin/src/dialogs2.jsx 333–416，但**只渲染真 API 响应字段派生的值**（F4）：
 * 原型的 24h 送达曲线 / 按设备渠道 / 错误 TOP / 漏斗中间过滤档（87% / 84%）全是合成数字，无契约统计端点
 * 支撑 → 一律移除，不显杜撰生产数据。当前可用真值：受众（≈ delivered+failed）/ 送达 / 状态；
 * 打开 / 打开率 / 跳转点击无埋点端点 → 渲染「--」（不显假 0、不算 NaN%）。
 * 接入推送投放统计端点（漏斗 / 渠道 / 错误码 / 时序曲线）后再补真值段（记 docs/plans §五）。
 * Stat 取自共享 ~/ui/StatCard；Drawer 取自 ~/ui/Dialog。
 */

import type { PushHistoryRow } from '~/data/queries';
import { fmtDateTime } from '~/lib/format';
import { Drawer } from '~/ui/Dialog';
import { Stat } from '~/ui/StatCard';

// 打开数无埋点端点时的统一占位（不显假 0 / 不算 NaN 开启率，与 PushPage 列表口径一致）
const NO_OPEN_DATA = '--';
const NO_OPEN_TITLE = '未接入 · 推送打开埋点暂无对应统计端点';

export function PushTaskDetail({ task, onClose }: { task: PushHistoryRow; onClose: () => void }) {
  const t = task;
  // 真 API 无打开埋点 → opened=null：打开 / 打开率 / 跳转点击均无真值，渲染「--」（不用 0 伪造、不算 NaN%）。
  const opened = t.opened;
  const hasOpenData = opened != null;
  // 送达率仅在有总受众（>0）时按真值派生；audience=0 时不算（避免 NaN%）。
  const deliveryRate =
    t.audience > 0 ? `${((t.delivered / t.audience) * 100).toFixed(1)}%` : NO_OPEN_DATA;

  return (
    <Drawer
      title={`推送任务 ${t.id}`}
      subtitle={`${t.type} · ${t.scope} · ${fmtDateTime(t.sentAt)}`}
      icon="IconPaperPlane"
      onClose={onClose}
      width={760}
    >
      {/* KPIs — 仅真 API 字段派生：受众 / 送达 / 送达率为真值；打开 / 打开率无埋点 → 「--」 */}
      <div
        style={{
          padding: 18,
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 14,
          borderBottom: '1px solid var(--border)',
        }}
      >
        <Stat label="受众" v={t.audience.toLocaleString()} />
        <Stat label="送达" v={t.delivered.toLocaleString()} />
        <Stat label="送达率" v={deliveryRate} />
        <Stat
          label="打开率"
          v={
            hasOpenData && t.delivered > 0
              ? `${((opened / t.delivered) * 100).toFixed(1)}%`
              : NO_OPEN_DATA
          }
        />
      </div>

      {/* F4：原型的 触达漏斗 / 24h 送达曲线 / 按设备渠道 / 错误 TOP 全为合成数字（无契约统计端点）→ 移除。
          仅保留下方诚实空态说明，避免运营误把样例数当真实投放分析。 */}
      <div style={{ padding: 18 }}>
        <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 8 }}>投放分析</div>
        <div
          className="card-flat"
          style={{
            padding: 14,
            background: 'var(--bg-subtle)',
            fontSize: 11.5,
            color: 'var(--fg-faint)',
            lineHeight: 1.6,
          }}
          title={NO_OPEN_TITLE}
        >
          触达漏斗 / 渠道拆分 / 错误码 TOP / 24h
          送达曲线暂无对应统计端点，接入推送投放统计端点后展示。 当前仅基于任务记录展示受众 /
          送达真值。
        </div>
      </div>
    </Drawer>
  );
}
